/**
 * Urban renewal eligibility: four checks, raw score, normalized scale.
 * Used by EligibilityCheck and buildUrbanixReportHtml — keep in sync.
 */
import { YesNoUnknownValues } from '../types/feasibility.js';

export const ELIGIBILITY_CARD_STATUS_LABELS = {
  pass: 'עומד בקריטריון',
  fail: 'לא עומד בקריטריון',
  unknown: 'נתון קריטי חסר',
};

/** Single intro for the eligibility section (UI + PDF): scope + limits, no duplicate disclaimer. */
export const ELIGIBILITY_SECTION_INTRO =
  'הבדיקה מציגה אינדיקציה ראשונית, מהירה ושקופה לגבי סבירות הזכאות לקידום התחדשות עירונית בנכס, לפי נתונים ממאגרים ציבוריים וכללי הערכה פנימיים של המערכת. אין כאן חוות דעת תכנונית, משפטית, שמאית או הנדסית; המידע אינו מחליף בדיקה מקצועית מלאה ואינו מהווה אישור רשמי.';

/** After eligibility summary list in EligibilityCheck: encourage generating the AI report. */
export const ELIGIBILITY_SUMMARY_AI_REPORT_NUDGE =
  'על בסיס הנתונים שנאספו, מומלץ להפיק דו"ח AI לצורך ניתוח מעמיק יותר של הממצאים, זיהוי הזדמנויות ומגבלות, והבנת המשמעויות התכנוניות של הנתונים שנאספו.';

/** Unknown cards + PDF: factual, concise missing-data explanation (no card title repeat). */
export const CRITICAL_UNKNOWN_IMPACT = {
  age: 'לא נמצא נתון מלא לגבי שנת הבנייה. ללא הנתון הזה קשה להעריך התאמה למסלולי התחדשות מסוימים ולבחון את רמת הכדאיות הראשונית.',
  plan: 'לא נמצא מידע ודאי על תכנית התחדשות רלוונטית. המשמעות היא שקיים חוסר ודאות תכנוני מהותי לגבי האפשרות לקדם פרויקט בנכס.',
  conservation:
    'לא נמצא מידע מלא לגבי סטטוס שימור. המשמעות היא שלא ניתן לשלול מראש מגבלות תכנוניות שעלולות להשפיע על היתכנות הפרויקט.',
  land_use:
    'לא נמצא ייעוד קרקע ברור. ללא המידע הזה לא ניתן לאמת התאמה בסיסית למסלול מגורים או למסלול התחדשות רלוונטי.',
};

const UNKNOWN_POINTS = -2;

export const normalizeEligibilityRawScore = (rawScore) => {
  if (rawScore >= 4) return 2;
  if (rawScore >= 2) return 1;
  if (rawScore >= 0) return 0;
  if (rawScore >= -3) return -1;
  return -2;
};

export const getYearEvidence = (year, confidence) => {
  if (!year || year === 1900) return null;
  if (confidence !== 'high' && confidence !== 'medium') return null;
  const currentYear = new Date().getFullYear();
  const buildingAge = currentYear - year;
  return {
    year_built: `${year}`,
    building_age: `${buildingAge}`,
  };
};

export const getPlanEvidenceLabel = (feasibilityInput) => {
  const section23Plan = feasibilityInput?.section23Plans?.[0];
  if (section23Plan?.planNumber) return `(${section23Plan.planNumber})`;
  if (section23Plan?.title) return `(${section23Plan.title})`;
  return '';
};

export const isResidentialLandUse = (landUse) => {
  if (!landUse) return false;
  const landUseLower = landUse.toLowerCase();
  return ['מגורים', 'דיור', 'מגורי'].some((keyword) => landUseLower.includes(keyword));
};

/**
 * @returns {{
 *   tests: Array<{ id, title, status, points, evidence, copyKey, missingImpact: string|null }>,
 *   rawScore: number,
 *   normalizedScore: number,
 *   hasAnyUnknown: boolean
 * }}
 */
export function computeEligibilityGoNoGo(feasibilityData) {
  const feasibilityInput = feasibilityData?.feasibilityInput || {};
  const feasibilityResult = feasibilityData?.feasibilityResult || {};
  const buildingInfo = feasibilityResult.buildingInfo || {};

  const yearConfidence =
    feasibilityInput.constructionYearConfidence || buildingInfo.confidence || 'unknown';
  const rawYear = feasibilityInput.constructionYear || buildingInfo.constructionYear;
  const yearEvidence = getYearEvidence(rawYear, yearConfidence);

  const pre1980Eligibility = feasibilityInput.pre1980Eligibility;
  const isPost1980 = yearEvidence
    ? Number(yearEvidence.year_built) >= 1980
    : pre1980Eligibility === YesNoUnknownValues.NO;

  let ageStatus = 'unknown';
  if (yearEvidence) {
    ageStatus = Number(yearEvidence.year_built) < 1980 ? 'pass' : 'fail';
  }

  const agePoints =
    ageStatus === 'pass' ? 1 : ageStatus === 'fail' ? -4 : UNKNOWN_POINTS;

  const declaredPinuyBinuyArea = feasibilityInput.declaredPinuyBinuyArea;
  const section7778Plans = feasibilityInput.section7778Plans || [];
  const has7778 = section7778Plans.length > 0;

  let planStatus = 'unknown';
  let planCopyKey = 'plan_unknown';
  if (declaredPinuyBinuyArea === YesNoUnknownValues.YES) planStatus = 'pass';
  if (declaredPinuyBinuyArea === YesNoUnknownValues.NO) planStatus = 'fail';

  if (has7778) {
    planStatus = 'fail';
    planCopyKey = 'plan_fail_7778';
  } else {
    planCopyKey = `plan_${planStatus}`;
  }

  const planEvidenceLabel = getPlanEvidenceLabel(feasibilityInput);
  const section7778Label = has7778
    ? ` (${section7778Plans[0].planNumber || section7778Plans[0].title})`
    : '';

  const planPoints =
    planStatus === 'pass' ? 1 : planStatus === 'fail' ? (isPost1980 ? -2 : 0) : UNKNOWN_POINTS;

  const conservationStatusValue =
    feasibilityInput.buildingConservationStatus?.hasConservation ??
    feasibilityInput.conservationListed ??
    buildingInfo.buildingConservationStatus?.hasConservation;
  const conservationStatus =
    conservationStatusValue === true
      ? 'fail'
      : conservationStatusValue === false
        ? 'pass'
        : 'unknown';
  const conservationPoints =
    conservationStatus === 'pass' ? 1 : conservationStatus === 'fail' ? -1 : UNKNOWN_POINTS;

  const landUse = feasibilityInput.landUse || buildingInfo.landUse;
  const landUseStatus = landUse
    ? isResidentialLandUse(landUse)
      ? 'pass'
      : 'fail'
    : 'unknown';
  const landUsePoints =
    landUseStatus === 'pass' ? 1 : landUseStatus === 'fail' ? -4 : UNKNOWN_POINTS;

  const rawScore = agePoints + planPoints + conservationPoints + landUsePoints;
  let normalizedScore = normalizeEligibilityRawScore(rawScore);

  const testsList = [
    {
      id: 'age',
      title: 'גיל המבנה',
      status: ageStatus,
      points: agePoints,
      evidence: yearEvidence,
      copyKey: `age_${ageStatus}`,
    },
    {
      id: 'plan',
      title: 'תכנית להתחדשות עירונית',
      status: planStatus,
      points: planPoints,
      evidence: { plan_name_or_id: planEvidenceLabel, section7778_plan_id: section7778Label },
      copyKey: planCopyKey,
    },
    {
      id: 'conservation',
      title: 'שימור',
      status: conservationStatus,
      points: conservationPoints,
      evidence: null,
      copyKey: `conservation_${conservationStatus}`,
    },
    {
      id: 'land_use',
      title: 'ייעוד קרקע',
      status: landUseStatus,
      points: landUsePoints,
      evidence: landUse ? { land_use: `(${landUse})` } : null,
      copyKey: `land_use_${landUseStatus}`,
    },
  ];

  const hasAnyUnknown = testsList.some((t) => t.status === 'unknown');
  if (hasAnyUnknown) {
    normalizedScore = Math.min(0, normalizedScore);
  }

  const tests = testsList.map((t) => ({
    ...t,
    missingImpact: t.status === 'unknown' ? CRITICAL_UNKNOWN_IMPACT[t.id] : null,
  }));

  return {
    tests,
    rawScore,
    normalizedScore,
    hasAnyUnknown,
  };
}
